import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { User, SkillStatus, SkillVariable } from '../types';
import { LanguageSkill, Language, LanguageSkillVariable } from '../types/language';
import { listLanguageSkills, createLanguageSkill, updateLanguageSkill, deleteLanguageSkill, submitLanguageSkill, withdrawLanguageSkill, approveLanguageSkill, rejectLanguageSkill, publishLanguageSkill, unpublishLanguageSkill, listLanguages } from '../api';
import Breadcrumb from '../components/Breadcrumb';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import SkillVariableEditor from '../components/SkillVariableEditor';
import { useToast } from '../components/Toast';
import { hasPermission } from '../utils/permissions';
import { sortPublishedFirst } from '../utils/sortSkills';

const emptyForm = { name: '', description: '', languageCode: '', systemPrompt: '', isDefault: false, variables: [] as SkillVariable[] };

const toSkillVariable = (v: LanguageSkillVariable): SkillVariable => ({
  key: v.key,
  label: v.label || v.key,
  type: 'text',
  required: false,
  default: v.default,
});

const toLanguageSkillVariable = (v: SkillVariable): LanguageSkillVariable => ({
  key: v.key,
  label: v.label,
  default: v.default,
});

const statusLabels: Record<SkillStatus, { text: string; color: string; bg: string }> = {
  draft: { text: 'draft', color: 'var(--text-2)', bg: 'var(--surface-2)' },
  pending_review: { text: 'pending_review', color: '#b45309', bg: '#fef3c7' },
  published: { text: 'published', color: '#15803d', bg: '#dcfce7' },
  unpublished: { text: 'unpublished', color: '#dc2626', bg: '#fee2e2' },
};

const LanguageSkillList: React.FC = () => {
  const { t } = useTranslation('skills');
  const toast = useToast();
  const navigate = useNavigate();
  const currentUser: User | null = JSON.parse(localStorage.getItem('user') || 'null');
  const canModerate = hasPermission(currentUser, 'operations');       // 审核/查看全部/管理员视图/published
  const canEditDefault = hasPermission(currentUser, 'platform_config'); // default 资产编辑
  const canEdit = (skill: LanguageSkill) => canModerate || (!!currentUser && skill.createdBy === currentUser.id);
  const isOwner = (skill: LanguageSkill) => !!currentUser && skill.createdBy === currentUser.id;
  const [skills, setSkills] = useState<LanguageSkill[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [filterLanguage, setFilterLanguage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSkill, setEditingSkill] = useState<LanguageSkill | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // confirm delete
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LanguageSkill | null>(null);

  // reject modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<LanguageSkill | null>(null);
  const [rejectNote, setRejectNote] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [skillData, langData] = await Promise.all([listLanguageSkills(), listLanguages()]);
      setSkills(skillData || []);
      setLanguages(langData || []);
    } catch (err: any) {
      toast.error(err.message || t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const languageName = (code: string) => languages.find(l => l.code === code)?.name || code;

  const openCreate = () => {
    setEditingSkill(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = (skill: LanguageSkill) => {
    setEditingSkill(skill);
    setForm({ name: skill.name, description: skill.description, languageCode: skill.languageCode, systemPrompt: skill.systemPrompt, isDefault: skill.isDefault, variables: (skill.variables || []).map(toSkillVariable) });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.languageCode) { setError(t('languageSkill.languageRequired')); return; }
    if (!form.name.trim()) { setError(t('common.nameRequired')); return; }
    setSaving(true);
    setError('');
    try {
      const slimVars: LanguageSkillVariable[] = form.variables.map(toLanguageSkillVariable);
      const payload = { ...form, variables: slimVars.length > 0 ? slimVars : undefined };
      if (editingSkill) {
        const updated = await updateLanguageSkill(editingSkill.id, payload);
        setSkills(prev => prev.map(s => s.id === editingSkill.id ? updated : updated.isDefault ? { ...s, isDefault: false } : s));
      } else {
        const created = await createLanguageSkill(payload);
        setSkills(prev => [...prev, created]);
      }
      setShowModal(false);
    } catch (err: any) {
      setError(err.message || t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (skill: LanguageSkill) => {
    if (skill.isDefault) { toast.warning(t('common.cannotDeleteDefault')); return; }
    setDeleteTarget(skill);
    setConfirmDelete(true);
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteLanguageSkill(deleteTarget.id);
      setSkills(prev => prev.filter(s => s.id !== deleteTarget.id));
    } catch (err: any) {
      toast.error(err.message || t('common.deleteFailed'));
    } finally {
      setConfirmDelete(false);
      setDeleteTarget(null);
    }
  };

  const handleSubmit = async (skill: LanguageSkill) => {
    try {
      const updated = await submitLanguageSkill(skill.id);
      setSkills(prev => prev.map(s => s.id === skill.id ? updated : s));
      toast.success(t('common.submitReviewSuccess'));
    } catch (err: any) {
      toast.error(err.message || t('common.submitFailed'));
    }
  };

  const handleWithdraw = async (skill: LanguageSkill) => {
    try {
      const updated = await withdrawLanguageSkill(skill.id);
      setSkills(prev => prev.map(s => s.id === skill.id ? updated : s));
      toast.success(t('common.withdrawSuccess'));
    } catch (err: any) {
      toast.error(err.message || t('common.withdrawFailed'));
    }
  };

  const handleApprove = async (skill: LanguageSkill) => {
    try {
      const updated = await approveLanguageSkill(skill.id);
      setSkills(prev => prev.map(s => s.id === skill.id ? updated : s));
      toast.success(t('common.approveSuccess'));
    } catch (err: any) {
      toast.error(err.message || t('common.operationFailed'));
    }
  };

  const openReject = (skill: LanguageSkill) => {
    setRejectTarget(skill);
    setRejectNote('');
    setShowRejectModal(true);
  };

  const doReject = async () => {
    if (!rejectTarget) return;
    if (!rejectNote.trim()) { toast.warning(t('common.rejectNoteRequired')); return; }
    try {
      const updated = await rejectLanguageSkill(rejectTarget.id, rejectNote);
      setSkills(prev => prev.map(s => s.id === rejectTarget.id ? updated : s));
      toast.success(t('common.rejected'));
      setShowRejectModal(false);
    } catch (err: any) {
      toast.error(err.message || t('common.operationFailed'));
    }
  };

  const handlePublish = async (skill: LanguageSkill) => {
    try {
      const updated = await publishLanguageSkill(skill.id);
      setSkills(prev => prev.map(s => s.id === skill.id ? updated : s));
      toast.success(t('common.republished'));
    } catch (err: any) {
      toast.error(err.message || t('common.operationFailed'));
    }
  };

  const handleUnpublish = async (skill: LanguageSkill) => {
    try {
      const updated = await unpublishLanguageSkill(skill.id);
      setSkills(prev => prev.map(s => s.id === skill.id ? updated : s));
      toast.success(t('common.unpublishedSuccess'));
    } catch (err: any) {
      toast.error(err.message || t('common.operationFailed'));
    }
  };

  const getStatus = (skill: LanguageSkill): SkillStatus => skill.status || 'published';

  // 语种筛选后按「已上架优先」排序
  const visibleSkills = sortPublishedFirst(
    skills.filter(s => !filterLanguage || s.languageCode === filterLanguage)
  );

  return (
    <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1rem' }}>
      <Breadcrumb items={[{ label: t('common.breadcrumbHome'), to: '/projects' }, { label: t('languageSkill.breadcrumbTitle') }]} />
      <div className="page-header">
        <h1 className="page-title">{t('languageSkill.pageTitle')}</h1>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {canEditDefault && (
            <button className="btn btn-secondary" onClick={() => navigate('/admin/languages')}>{t('languageSkill.languageManage')}
            </button>
          )}
          <button className="btn btn-primary" onClick={openCreate}>{t('languageSkill.createBtn')}</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
        <label className="form-label" style={{ margin: 0 }}>{t('languageSkill.filterLabel')}</label>
        <select className="input" style={{ width: 'auto' }} value={filterLanguage} onChange={e => setFilterLanguage(e.target.value)}>
          <option value="">{t('languageSkill.filterAll')}</option>
          {languages.map(l => <option key={l.id} value={l.code}>{l.name}</option>)}
        </select>
      </div>

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {visibleSkills.length === 0 && <p style={{ color: 'var(--text-2)' }}>{t('languageSkill.empty')}</p>}
          {visibleSkills.map(skill => {
            const status = getStatus(skill);
            const label = statusLabels[status];
            return (
            <div key={skill.id} className="list-item" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: '1rem' }}>{skill.name}</span>
                  {skill.languageCode && <span style={{ background: 'var(--surface-2)', color: 'var(--text-2)', fontSize: '0.7rem', padding: '1px 6px', borderRadius: '10px' }}>{languageName(skill.languageCode)}</span>}
                  {skill.isDefault && <span style={{ background: 'var(--primary)', color: '#fff', fontSize: '0.7rem', padding: '1px 6px', borderRadius: '10px' }}>{t('common.defaultBadge')}</span>}
                  <span style={{ fontSize: '0.7rem', padding: '1px 8px', borderRadius: '10px', color: label.color, background: label.bg }}>{t('status.' + label.text)}</span>
                </div>
                {skill.description && <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: 'var(--text-2)' }}>{skill.description}</p>}
                {skill.reviewComment && status === 'draft' && (
                  <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.8rem', color: '#dc2626', background: '#fee2e2', padding: '0.3rem 0.5rem', borderRadius: '4px' }}>
                    {t('common.rejectReason')}{skill.reviewComment}
                  </p>
                )}
                {skill.systemPrompt && (
                  <pre style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-2)', background: 'var(--surface-2)', padding: '0.5rem', borderRadius: '4px', whiteSpace: 'pre-wrap', maxHeight: '80px', overflow: 'hidden' }}>
                    {skill.systemPrompt.slice(0, 200)}{skill.systemPrompt.length > 200 ? '...' : ''}
                  </pre>
                )}
              </div>
              <div className="list-item-actions" style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {isOwner(skill) && (status === 'draft' || status === 'unpublished') && (
                  <button className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={() => handleSubmit(skill)}>{t('common.submitReview')}</button>
                )}
                {isOwner(skill) && status === 'pending_review' && (
                  <button className="btn btn-ghost" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={() => handleWithdraw(skill)}>{t('common.withdraw')}</button>
                )}
                {canModerate && status === 'pending_review' && (
                  <>
                    <button className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={() => handleApprove(skill)}>{t('common.approve')}</button>
                    <button className="btn btn-danger" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={() => openReject(skill)}>{t('common.reject')}</button>
                  </>
                )}
                {canModerate && status === 'published' && !skill.isDefault && (
                  <button className="btn btn-ghost" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem', color: '#dc2626' }} onClick={() => handleUnpublish(skill)}>{t('common.unpublish')}</button>
                )}
                {canModerate && status === 'unpublished' && (
                  <button className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={() => handlePublish(skill)}>{t('common.republish')}</button>
                )}
                {canEdit(skill) && (status === 'draft' || status === 'unpublished' || canModerate) && (
                  <button className="btn btn-ghost" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={() => openEdit(skill)}>{t('common.edit')}</button>
                )}
                {canEdit(skill) && (status !== 'published' || canModerate) && (
                  <button className="btn btn-danger" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={() => handleDelete(skill)} disabled={skill.isDefault}>{t('common.delete')}</button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingSkill ? t('languageSkill.editTitle') : t('languageSkill.createTitle')}
        width="800px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>{t('common.cancel')}</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? t('common.saving') : t('common.save')}</button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">{t('languageSkill.languageLabel')}</label>
          <select className="input" value={form.languageCode} onChange={e => setForm({ ...form, languageCode: e.target.value })} disabled={!!editingSkill}>
            <option value="">{t('languageSkill.languagePlaceholder')}</option>
            {languages.filter(l => l.enabled).map(l => <option key={l.id} value={l.code}>{l.name}</option>)}
          </select>
          {editingSkill && <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--text-2)' }}>{t('languageSkill.languageImmutable')}</p>}
        </div>
        <div className="form-group">
          <label className="form-label">{t('common.nameLabel')}</label>
          <input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={t('languageSkill.namePlaceholder')} />
        </div>
        <div className="form-group">
          <label className="form-label">{t('common.descLabel')}</label>
          <input className="input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder={t('common.descPlaceholder')} />
        </div>
        <div className="form-group">
          <label className="form-label" style={{ marginBottom: '0.25rem' }}>{t('languageSkill.systemPromptLabel')}</label>
          <textarea className="input textarea" style={{ minHeight: '200px', fontFamily: 'monospace', fontSize: '0.85rem' }} value={form.systemPrompt} onChange={e => setForm({ ...form, systemPrompt: e.target.value })} placeholder={t('languageSkill.systemPromptPlaceholder')} />
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--text-2)' }}>
            {t('languageSkill.systemPromptHint')}
          </p>
        </div>
        <div className="form-group">
          <label className="form-label">{t('common.templateVariables')}</label>
          <SkillVariableEditor
            variables={form.variables}
            onChange={variables => setForm({ ...form, variables })}
            systemPrompt={form.systemPrompt}
          />
        </div>
        {canEditDefault && (
        <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input type="checkbox" id="languageIsDefault" checked={form.isDefault} onChange={e => setForm({ ...form, isDefault: e.target.checked })} />
          <label htmlFor="languageIsDefault" style={{ margin: 0, cursor: 'pointer' }}>{t('languageSkill.setDefaultLanguage')}</label>
        </div>
        )}
        {error && <p className="error-text">{error}</p>}
      </Modal>

      {/* 驳回原因弹窗 */}
      <Modal
        open={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title={t('common.rejectSkillTitle')}
        width="500px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowRejectModal(false)}>{t('common.cancel')}</button>
            <button className="btn btn-danger" onClick={doReject}>{t('common.confirmReject')}</button>
          </>
        }
      >
        <p style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
          {t('common.rejectPrompt', { name: '' })}<strong>{rejectTarget?.name}</strong>
        </p>
        <textarea
          className="input textarea"
          style={{ minHeight: '100px' }}
          value={rejectNote}
          onChange={e => setRejectNote(e.target.value)}
          placeholder={t('common.rejectPlaceholder')}
        />
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        onCancel={() => { setConfirmDelete(false); setDeleteTarget(null); }}
        onConfirm={doDelete}
        title={t('common.deleteSkillTitle')}
        message={<>{t('common.deleteConfirm', { name: '' })}<strong>{deleteTarget?.name}</strong></>}
        confirmText={t('common.confirmDelete')}
        confirmDanger
      />
    </div>
  );
};

export default LanguageSkillList;
